'use client';

import React, { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { createClient } from '@/utils/supabase/client';
import type { EmailOtpType } from '@supabase/supabase-js';

function ConfirmHandler() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [countdown, setCountdown] = useState(2);

  // Auto redirect countdown to /login
  useEffect(() => {
    if (!isVerified) return;
    if (countdown <= 0) {
      router.replace(`/login?verified=true&email=${encodeURIComponent(verifiedEmail)}`);
      return;
    }
    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [isVerified, countdown, router, verifiedEmail]);

  useEffect(() => {
    const handleConfirm = async () => {
      const token_hash = searchParams.get('token_hash');
      const type = searchParams.get('type') as EmailOtpType | null;

      const lastStoredEmail = typeof window !== 'undefined'
        ? (localStorage.getItem('kheldrop_last_signup_email') || localStorage.getItem('kheldrop_verified_email') || '')
        : '';

      if (!token_hash || !type) {
        if (lastStoredEmail) {
          setVerifiedEmail(lastStoredEmail);
          setIsVerified(true);
          return;
        }
        router.replace('/auth-error?error=expired');
        return;
      }

      const supabase = createClient();
      const { data, error } = await supabase.auth.verifyOtp({ type, token_hash });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('already') || msg.includes('expired')) {
          if (lastStoredEmail) {
            localStorage.setItem('kheldrop_verified_email', lastStoredEmail);
          }
          setVerifiedEmail(lastStoredEmail);
          setIsVerified(true);
          return;
        }
        setErrorMsg(error.message);
        router.replace(`/auth-error?error=expired&message=${encodeURIComponent(error.message)}`);
      } else if (type === 'recovery') {
        router.replace('/reset-password');
      } else {
        const emailResolved =
          data?.user?.email ||
          searchParams.get('email') ||
          lastStoredEmail ||
          '';

        if (emailResolved && typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_verified_email', emailResolved);
        }

        if (data?.user && typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_auth_user', JSON.stringify(data.user));
          if (data.session) {
            localStorage.setItem('kheldrop_token', data.session.access_token);
          }
        }

        setVerifiedEmail(emailResolved);
        setIsVerified(true);
      }
    };

    handleConfirm();
  }, [router, searchParams]);

  if (isVerified) {
    return (
      <div style={{
        minHeight: '75vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}>
        <div style={{
          maxWidth: '460px',
          width: '100%',
          background: 'linear-gradient(180deg, rgba(22, 18, 42, 0.96) 0%, rgba(12, 13, 24, 0.98) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.45)',
          borderRadius: '24px',
          padding: '38px 28px',
          textAlign: 'center',
          boxShadow: '0 24px 50px rgba(0, 0, 0, 0.7), 0 0 40px rgba(16, 185, 129, 0.15)',
          backdropFilter: 'blur(20px)',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.18)',
            border: '2px solid rgba(16, 185, 129, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 22px',
            boxShadow: '0 0 28px rgba(16, 185, 129, 0.4)',
          }}>
            <CheckCircle2 size={40} color="#10B981" />
          </div>

          <h2 style={{
            fontSize: '24px',
            fontWeight: 800,
            color: '#FFFFFF',
            margin: '0 0 10px',
            letterSpacing: '-0.02em',
          }}>
            Email Verified Successfully!
          </h2>

          <p style={{
            fontSize: '14px',
            color: '#94A3B8',
            margin: '0 0 20px',
            lineHeight: 1.55,
          }}>
            Your KhelDrop account email has been verified. Redirecting you to Sign In with your email pre-filled.
          </p>

          {verifiedEmail && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '12px',
              padding: '10px 16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13.5px',
              fontWeight: 600,
              color: '#F1F5F9',
              marginBottom: '26px',
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
              <span>{verifiedEmail}</span>
            </div>
          )}

          <div style={{
            fontSize: '12px',
            color: '#64748B',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}>
            <span>Redirecting to Sign In in {countdown}s...</span>
          </div>

          <button
            onClick={() => router.replace(`/login?verified=true&email=${encodeURIComponent(verifiedEmail)}`)}
            style={{
              width: '100%',
              padding: '13px 20px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: '#FFFFFF',
              border: 'none',
              fontSize: '14.5px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 18px rgba(16, 185, 129, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            <span>Continue to Sign In</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ maxWidth: '420px', textAlign: 'center', color: '#EF4444' }}>
          <AlertCircle size={36} color="#EF4444" style={{ margin: '0 auto 12px' }} />
          <h3>Confirmation Error</h3>
          <p style={{ color: '#94A3B8', fontSize: '14px' }}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '80px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
      <div style={{
        width: '36px',
        height: '36px',
        border: '3px solid rgba(139, 92, 246, 0.2)',
        borderTopColor: '#8B5CF6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px',
      }} />
      <p style={{ fontSize: '14px' }}>Verifying confirmation link…</p>
    </div>
  );
}

export default function AuthConfirmPage() {
  return (
    <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>Loading...</div>}>
      <ConfirmHandler />
    </Suspense>
  );
}
